import { Button, Dialog, DialogActions, DialogTitle } from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { useIdempotencyKey } from '../../../../hooks/useIdempotencyKey';
import { useTerminateWebhookMutation } from '../../../../api/webhooks';
import { useCallback, useEffect } from 'react';
import { ApiCustomerWebhook } from '../../../../openapi';

export function DeleteWebhookDialog({
  webhook,
  onCancel,
  onSuccess,
}: {
  webhook: ApiCustomerWebhook | undefined;
  onCancel: () => void;
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const idempotencyKey = useIdempotencyKey();
  const deleteMutation = useTerminateWebhookMutation({ idempotencyKey });

  const handleCancel = useCallback(() => {
    deleteMutation.reset();
    onCancel();
  }, [deleteMutation, onCancel]);

  const handleConfirm = useCallback(() => {
    if (webhook) {
      deleteMutation.mutate({ id: webhook.id });
    }
  }, [webhook, deleteMutation]);

  useEffect(() => {
    if (deleteMutation.isSuccess) {
      onCancel();
      onSuccess?.();
      deleteMutation.reset();
    }
  }, [deleteMutation, onCancel, onSuccess]);

  return (
    <Dialog open={Boolean(webhook)} fullWidth maxWidth="sm" onClose={handleCancel}>
      <DialogTitle>{t('Are you sure you want to delete this webhook?')}</DialogTitle>
      <DialogActions>
        <Button autoFocus color="secondary" onClick={handleCancel}>
          {t('Cancel')}
        </Button>
        <Button color="error" loading={deleteMutation.isPending} onClick={handleConfirm}>
          {t('Delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
